import React, { useMemo, useState, Component } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import useStrategyData from './hooks/useStrategyData';
import useBasket from './hooks/useBasket';
import OverviewPage from './OverviewPage';
import ComparisonPage from './ComparisonPage';
import CorrelationPage from './CorrelationPage';
import DrawdownPage from './DrawdownPage';
import RollingPage from './RollingPage';
import CalendarPage from './CalendarPage';
import StrategyDetailPage from './StrategyDetailPage';
import PortfolioBuilder from './PortfolioBuilder';
import PortfolioOptimizerPage from './PortfolioOptimizerPage';

// Error boundary to prevent analytics crashes from taking down the whole page
class AnalyticsErrorBoundary extends Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 text-center">
          <p className="text-red-400 text-sm mb-2">Something went wrong rendering this page.</p>
          <p className="text-xs text-gray-500">{this.state.error?.message}</p>
          <button onClick={() => this.setState({ hasError: false })} className="mt-3 text-xs text-brand-gold hover:underline">Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

const inputCls = 'w-full bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-xs text-gray-800 dark:text-gray-200 outline-none focus:ring-2 focus:ring-brand-gold/30';

const StrategyAnalyticsLayout = () => {
  const location = useLocation();
  const subPath = location.pathname.replace('/trading/analytics', '').replace(/^\//, '') || 'overview';

  const {
    source, strategyFilter, symbolFilter, dateRange, rfRate, benchmark, focus,
    availableStrategies, loading, error,
    setSource, setStrategyFilter, setSymbolFilter, setDateRange, setRfRate, setBenchmark, setFocus,
    getAnalyticsParams, cachedFetch,
  } = useStrategyData();

  // Portfolio basket (pinned strategies)
  const { basket, toggle: togglePin, clear: clearBasket } = useBasket();
  const [builderOpen, setBuilderOpen] = useState(false);

  // Lifted analytics data from OverviewPage so PortfolioBuilder can use it
  const [analyticsData, setAnalyticsData] = useState(null);

  // Normalize the cumulative response to the shape PortfolioBuilder expects:
  // { index: [...dates], columns: [...names], data: [[...values], ...] }
  const cumData = useMemo(() => {
    const raw = analyticsData?.cumulative;
    return (raw?.cumulative || raw) || null;
  }, [analyticsData]);

  // Track available strategy labels from the last API response (for focus dropdown)
  const [strategyLabels, setStrategyLabels] = React.useState([]);

  // Refresh counter — incrementing forces a re-fetch even if filters haven't changed
  const [refreshKey, setRefreshKey] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const paramsKey = JSON.stringify({ source, strategyFilter, symbolFilter, ds: dateRange.start, de: dateRange.end, rfRate, benchmark, focus, _r: refreshKey });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const analyticsParams = useMemo(() => getAnalyticsParams(), [paramsKey]);

  return (
    <div className="space-y-4">
      {/* Filter panel */}
      <div className="bg-white dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Filters</span>
          <button
            onClick={() => { setAnalyticsData(null); setRefreshKey(k => k + 1); }}
            title="Reload analytics data (e.g. after saving a new strategy)"
            className="text-xs text-gray-400 hover:text-brand-gold transition-colors flex items-center gap-1"
          >
            ↺ Refresh
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {/* Source */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Source</label>
            <select value={source} onChange={e => setSource(e.target.value)} className={inputCls}>
              <option value="backtest">Backtests</option>
              <option value="simulation">Portfolio Simulations</option>
            </select>
          </div>

          {/* Strategy filter */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Strategy</label>
            <select value={strategyFilter} onChange={e => setStrategyFilter(e.target.value)} className={inputCls}>
              <option value="">All Strategies</option>
              {availableStrategies.map(s => (
                <option key={s.key || s.name} value={s.key || s.name}>{s.name || s.key}</option>
              ))}
            </select>
          </div>

          {/* Symbol filter */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Symbol</label>
            <input
              type="text"
              value={symbolFilter}
              onChange={e => setSymbolFilter(e.target.value.toUpperCase())}
              placeholder="e.g. AAPL"
              className={inputCls}
            />
          </div>

          {/* Date range */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.start || ''}
              onChange={e => setDateRange(prev => ({ ...prev, start: e.target.value || null }))}
              className={inputCls}
            />
          </div>

          {/* Benchmark */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Benchmark</label>
            <select value={benchmark} onChange={e => setBenchmark(e.target.value)} className={inputCls}>
              <option value="SPY">SPY</option>
              <option value="QQQ">QQQ</option>
              <option value="">None</option>
            </select>
          </div>

          {/* Focus strategy — sets inception period for charts */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Focus Strategy</label>
            <select value={focus} onChange={e => setFocus(e.target.value)} className={inputCls}>
              <option value="">None</option>
              {strategyLabels.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* Risk-free rate */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Rf Rate: {(rfRate * 100).toFixed(1)}%</label>
            <input
              type="range"
              min="0" max="0.10" step="0.005"
              value={rfRate}
              onChange={e => setRfRate(parseFloat(e.target.value))}
              className="w-full accent-brand-gold"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-2.5 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Navigation tabs */}
      <nav className="flex overflow-x-auto gap-1 border-b border-gray-200 dark:border-gray-800">
        {[
          { to: '/trading/analytics', label: 'Overview', end: true },
          { to: '/trading/analytics/detail', label: 'Strategy Detail' },
          { to: '/trading/analytics/comparison', label: 'Comparison' },
          { to: '/trading/analytics/correlation', label: 'Correlation' },
          { to: '/trading/analytics/drawdown', label: 'Drawdown' },
          { to: '/trading/analytics/rolling', label: 'Rolling' },
          { to: '/trading/analytics/calendar', label: 'Calendar' },
          { to: '/trading/analytics/optimizer', label: 'Portfolio Optimizer' },
        ].map(tab => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              `px-3 py-2 text-sm whitespace-nowrap transition-colors ${
                isActive
                  ? 'border-b-2 border-brand-gold text-brand-gold'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      {/* Pages — wrapped in error boundary to prevent crashes from blanking the whole app */}
      <AnalyticsErrorBoundary key={subPath}>
        {(subPath === 'overview' || subPath === '') && (
          <OverviewPage
            analyticsParams={analyticsParams}
            paramsKey={paramsKey}
            focus={focus}
            onLabelsLoaded={setStrategyLabels}
            basket={basket}
            onTogglePin={togglePin}
            onDataLoaded={setAnalyticsData}
          />
        )}
        {subPath === 'detail' && <StrategyDetailPage analyticsParams={analyticsParams} paramsKey={paramsKey} />}
        {subPath === 'comparison' && <ComparisonPage analyticsParams={analyticsParams} paramsKey={paramsKey} />}
        {subPath === 'correlation' && <CorrelationPage analyticsParams={analyticsParams} paramsKey={paramsKey} />}
        {subPath === 'drawdown' && <DrawdownPage analyticsParams={analyticsParams} paramsKey={paramsKey} />}
        {subPath === 'rolling' && <RollingPage analyticsParams={analyticsParams} paramsKey={paramsKey} />}
        {subPath === 'calendar' && <CalendarPage analyticsParams={analyticsParams} paramsKey={paramsKey} />}
        {subPath === 'optimizer' && <PortfolioOptimizerPage analyticsData={analyticsData} />}
      </AnalyticsErrorBoundary>

      {/* Floating Portfolio Builder chip — visible on all analytics sub-pages when basket has items */}
      {basket.size > 0 && (
        <button
          onClick={() => setBuilderOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand-gold text-white text-sm font-semibold shadow-xl hover:brightness-110 transition-all"
        >
          <span>📊</span>
          <span>Portfolio Builder</span>
          <span className="bg-white/25 text-white text-xs font-bold px-1.5 py-0.5 rounded-full leading-none">
            {basket.size}
          </span>
        </button>
      )}

      {/* Portfolio Builder drawer */}
      {builderOpen && (
        <PortfolioBuilder
          basket={basket}
          onRemove={togglePin}
          onClear={clearBasket}
          onClose={() => setBuilderOpen(false)}
          cumData={cumData}
          summaryData={analyticsData?.summary}
        />
      )}
    </div>
  );
};

export default StrategyAnalyticsLayout;
